import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  IconButton,
  Card,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  FaUserPlus,
  FaFileAlt,
  FaClipboardCheck,
  FaMoneyCheckAlt,
  FaEnvelope,
  FaStore,
  FaClipboardList,
  FaCheckCircle,
  FaArchive,
  FaUndo,
  FaEdit
} from 'react-icons/fa';
import {
  AlertCircle,
  Clock,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AuthContext } from '../context/AuthContext';
import { getFriendlyFieldName } from '../utils/fieldNameMapping';
import axiosInstance from './axiosInstance';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [payrollNotes, setPayrollNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userRole, user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get user data from localStorage
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        console.log('User data for activity feed:', userData);
        
        // Execute both fetch operations in parallel
        const [activitiesData, payrollNotesData] = await Promise.all([
          fetchActivities(userData),
          fetchPayrollNotes()
        ]);
        
        // Combine and process the data
        setActivities(activitiesData);
        setPayrollNotes(payrollNotesData);
      } catch (err) {
        console.error("Error fetching activity data:", err);
        setError("Failed to load activity feed. Please try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };
    
    // Fetch regular activities
    const fetchActivities = async (userData) => {
      // Adjust endpoint based on user role (admin sees all, manager sees their store)
      let endpoint = `${API_URL}/activity-feed`;
      
      // For managers, add locationId query parameter based on user data
      if (userData.role === 'manager' && userData.store) {
        endpoint += `?locationId=${userData.store}`;
        console.log('Manager view: filtering by locationId:', userData.store);
      }
      
      console.log('Fetching activities from:', endpoint);
      
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch activity feed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Activity data received:', data.length, 'items');
      return data;
    };
    
    // Fetch payroll notes
    const fetchPayrollNotes = async () => {
      try {
        if (userRole !== 'admin' && userRole !== 'manager') {
          return []; // Only fetch for admins and managers
        }
        
        const response = await axiosInstance.get(`${API_URL}/payroll_notes`);
        
        // Get only notes that have been acknowledged in the last 7 days
        const now = new Date();
        const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7));
        
        const recentAcknowledgedNotes = response.data
          .filter(note => 
            note.isAcknowledged && 
            new Date(note.acknowledgedAt) > sevenDaysAgo
          )
          .sort((a, b) => new Date(b.acknowledgedAt) - new Date(a.acknowledgedAt));
        
        return recentAcknowledgedNotes;
      } catch (err) {
        console.error('Error fetching payroll notes:', err);
        return [];
      }
    };

    fetchData();
    
    // Set up refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      fetchData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [userRole]);

  // Function to get friendly names for display
  const getFriendlyName = (type) => {
    if (!type) return '';
    
    const friendlyNames = {
      'onboarding': 'Onboarding',
      'letter': 'Letter',
      'form': 'Form',
      'form_update': 'Form Update',
      'archive': 'Archive',
      'reinstate': 'Reinstate',
      'check_request': 'Check Request',
      'paper_check': 'Paper Check',
      'direct_deposit': 'Direct Deposit',
      'mailed_check': 'Mailed Check',
      'stop_payment': 'Stop Payment',
      'reissue': 'Reissue Check',
      'password_reset': 'Password Reset'
    };
    
    // Convert snake_case to Title Case if not found in mapping
    if (!friendlyNames[type]) {
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    return friendlyNames[type];
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'onboarding':
        return <FaUserPlus size={18} color="#4caf50" />;
      case 'letter':
        return <FaEnvelope size={18} color="#2196f3" />;
      case 'form':
        return <FaClipboardCheck size={18} color="#ff9800" />;
      case 'form_update':
        return <FaEdit size={18} color="#ff9800" />;
      case 'archive':
        return <FaArchive size={18} color="#f44336" />;
      case 'reinstate':
        return <FaUndo size={18} color="#4caf50" />;
      case 'payroll_note':
        return <FaClipboardList size={18} color="#ed6c02" />;
      case 'check_request':
      case 'paper_check':
      case 'direct_deposit':
      case 'mailed_check':
      case 'stop_payment':
      case 'reissue':
        return <FaMoneyCheckAlt size={18} color="#9c27b0" />;
      default:
        return <FaFileAlt size={18} color="#607d8b" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'onboarding':
        return '#e8f5e9';
      case 'letter':
        return '#e3f2fd';
      case 'form':
        return '#fff3e0';
      case 'payroll_note':
        return '#fff8e1';
      case 'check_request':
        return '#f3e5f5';
      default:
        return '#f5f5f5';
    }
  };

  const getTagColor = (tag) => {
    // Handle common status tags
    const lowercase = tag?.toLowerCase() || '';
    
    // Handle employee-related tags
    if (lowercase.includes('new employee') || lowercase === 'new employee') return '#00bfa5'; // Teal accent
    if (lowercase.includes('employee')) return '#00897b'; // Teal
    
    // Handle note-related tags with a nice blue color
    if (lowercase.includes('note') || lowercase === 'note added') return '#1976d2';
    
    if (lowercase.includes('complete') || lowercase === 'sent' || lowercase === 'approved' || lowercase === 'acknowledged') return '#4caf50';
    if (lowercase.includes('pending')) return '#ff9800';
    if (lowercase.includes('failed') || lowercase.includes('error') || lowercase === 'denied') return '#f44336';
    if (lowercase.includes('waiting')) return '#2196f3';
    if (lowercase.includes('archive')) return '#9c27b0';
    if (lowercase.includes('reinstat')) return '#00897b';
    
    // Handle reissue methods
    if (lowercase === 'direct deposit') return '#9c27b0';
    if (lowercase === 'mailed check') return '#00bcd4';
    
    // Handle role tags
    if (lowercase === 'admin' || lowercase === 'manager' || lowercase === 'user') return '#6366f1'; // Indigo
    
    // Default color
    return '#757575';
  };
  
  // Convert payroll notes to activity format
  const convertToActivity = (note) => {
    return {
      id: `note_${note.id}`,
      type: 'payroll_note',
      title: note.subject,
      // Show full content; let UI wrap naturally
      description: note.isAcknowledged 
        ? `Note from ${note.employeeName} was acknowledged by ${note.acknowledgedByName}` 
        : (note.content || ''),
      timestamp: note.isAcknowledged ? note.acknowledgedAt : note.createdAt,
      tags: [note.isAcknowledged ? 'acknowledged' : 'pending'],
      location: note.locationName
    };
  };

  const getFormattedTime = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return 'Unknown date';
    }
  };

  // Determine if an activity requires admin acknowledgment
  const requiresAcknowledgment = (activity) => {
    const acknowledgmentTypes = ['archive', 'reinstate', 'form_update'];
    return acknowledgmentTypes.includes(activity.type) && userRole === 'admin';
  };

  if (loading) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          height: '100%',
          borderRadius: 2,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#3B82F6', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading activity feed...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          height: '100%',
          borderRadius: 2,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ 
          p: 4, 
          color: 'error.main',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}>
          <AlertCircle size={32} color="#f44336" />
          <Typography variant="body1" color="inherit" sx={{ fontWeight: 500 }}>{error}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid #E5E7EB',
      }}
    >
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#d6e8ed',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center">
          <Clock size={isMobile ? 16 : 20} color="#2563EB" style={{ marginRight: 8 }} />
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ 
              fontWeight: 600,
              color: '#1F2937',
              fontSize: isMobile ? '1rem' : undefined
            }}
          >
            Activity Feed
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ 
            color: '#0C4A6E',
            fontWeight: 500,
            fontSize: '0.75rem'
          }}
        >
          {activities.length} items
        </Typography>
      </Box>
      
      <Box sx={{ 
        overflowY: 'auto', 
        flex: 1,
        bgcolor: '#fff' 
      }}>
        {activities.length > 0 || payrollNotes.length > 0 ? (
          <List sx={{ p: 0 }}>
            {/* Convert and include payroll notes */}
            {[...activities, ...payrollNotes.map(convertToActivity)]
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((activity, index) => (
              <React.Fragment key={`${activity.type}_${activity.id || index}`}>
                <ListItem
                  sx={{
                    p: 0,
                    borderBottom: index < activities.length - 1 ? '1px solid #F3F4F6' : 'none',
                    '&:hover': {
                      bgcolor: '#F9FAFB'
                    }
                  }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      width: '100%',
                      boxShadow: 'none',
                      borderRadius: 0,
                      border: 'none',
                      '&:hover': {
                        bgcolor: '#F9FAFB'
                      }
                    }}
                  >
                    <Box sx={{ p: { xs: 2, sm: 2 } }}>
                      {/* Mobile Layout */}
                      {isMobile ? (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2
                        }}>
                          {/* Icon Column */}
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: getActivityColor(activity.type),
                              flexShrink: 0,
                              mt: 0.5
                            }}
                          >
                            {getActivityIcon(activity.type)}
                          </Avatar>
                          
                          {/* Content Column */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Title and Time Row */}
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 0.5
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: '#1F2937',
                                  flex: 1,
                                  lineHeight: 1.4
                                }}
                              >
                                {activity.title && activity.title.includes('_') ? getFriendlyFieldName(activity.title) || getFriendlyName(activity.title) : activity.title}
                              </Typography>
                              {/* Primary status tag */}
                              {activity.tags && activity.tags.length > 0 && (
                                <Chip 
                                  label={getFriendlyFieldName(activity.tags[0]) || getFriendlyName(activity.tags[0])}
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    bgcolor: getTagColor(activity.tags[0]),
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    px: 0.75
                                  }}
                                />
                              )}
                            </Box>
                            
                            {/* Timestamp */}
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '0.75rem',
                                display: 'block',
                                mb: 0.75
                              }}
                            >
                              {getFormattedTime(activity.timestamp)}
                            </Typography>
                            
                            {/* Description */}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6B7280', 
                                mb: 1,
                                fontSize: '0.813rem',
                                lineHeight: 1.5
                              }}
                            >
                              {activity.description && activity.description.includes('old_address') 
                                ? activity.description.replace('old_address', 'previous address')
                                : activity.description}
                            </Typography>
                            
                            {/* Location and additional tags */}
                            {(activity.location || (activity.tags && activity.tags.length > 1)) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                {activity.location && (
                                  <Chip 
                                    icon={<FaStore size={10} />} 
                                    label={activity.location}
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      bgcolor: '#F3F4F6',
                                      color: '#6B7280',
                                      fontSize: '0.65rem',
                                      fontWeight: 500,
                                      '& .MuiChip-icon': { 
                                        color: '#6B7280',
                                        mr: 0.25
                                      }
                                    }}
                                  />
                                )}
                                
                                {activity.tags && activity.tags.slice(1).map(tag => (
                                  <Chip 
                                    key={tag} 
                                    label={getFriendlyFieldName(tag) || getFriendlyName(tag)}
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      bgcolor: '#F3F4F6',
                                      color: '#6B7280',
                                      fontSize: '0.65rem',
                                      fontWeight: 500
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {/* Desktop Layout - Original */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: 1 
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  mr: 1.5,
                                  bgcolor: 'white',
                                  border: `1px solid ${getActivityColor(activity.type)}`,
                                  color: 'primary.main',
                                }}
                              >
                                {getActivityIcon(activity.type)}
                              </Avatar>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                                {activity.title && activity.title.includes('_') ? getFriendlyFieldName(activity.title) || getFriendlyName(activity.title) : activity.title}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {getFormattedTime(activity.timestamp)}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" sx={{ color: '#4B5563', mb: 1, pl: 5 }}>
                            {activity.description && activity.description.includes('old_address') 
                              ? activity.description.replace('old_address', 'previous address')
                              : activity.description}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, pl: 5 }}>
                            {activity.location && (
                              <Chip 
                                icon={<FaStore size={12} />} 
                                label={activity.location}
                                size="small"
                                sx={{ 
                                  height: 24,
                                  bgcolor: '#F3F4F6',
                                  color: '#4B5563',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  '& .MuiChip-icon': { 
                                    color: '#6B7280',
                                    mr: 0.5 
                                  }
                                }}
                              />
                            )}
                            
                            {activity.tags && Array.isArray(activity.tags) && activity.tags.map(tag => (
                              <Chip 
                                key={tag} 
                                label={getFriendlyFieldName(tag) || getFriendlyName(tag)}
                                size="small"
                                sx={{ 
                                  height: 24,
                                  bgcolor: getTagColor(tag),
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Card>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Clock size={36} color="#9CA3AF" style={{ marginBottom: 16 }} />
            <Typography variant="body1" sx={{ color: '#6B7280', fontWeight: 500 }}>
              No recent activities to display
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Recent actions will appear here
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ActivityFeed;
